import { Section } from "@/layouts/Section";

export default function Terms() {
  return (
    <div>
      <Section variant="light">
        <div className="max-w-[720px] mx-auto">
          <div className="section-label">Legal</div>
          <h1 className="section-heading mb-3">Terms of Service</h1>
          <p className="text-sm text-muted-foreground m-0">Effective: May 2026 &mdash; Version 2026-05</p>
        </div>
      </Section>

      <Section variant="muted">
        <div className="max-w-[720px] mx-auto flex flex-col gap-10">

          <div>
            <h2 className="text-lg font-semibold text-foreground mb-3">1. What EasyReports Is</h2>
            <p className="text-base leading-[1.75] text-muted-foreground m-0">
              EasyReports (&ldquo;the Service&rdquo;) is a web-based platform that helps Ontario educators create,
              manage, and export student report cards. The Service is operated by EasyReports
              (&ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;). By creating an account or using the Service you agree to
              be bound by these Terms of Service (&ldquo;Terms&rdquo;).
            </p>
          </div>

          <div>
            <h2 className="text-lg font-semibold text-foreground mb-3">2. Who Can Use the Service</h2>
            <p className="text-base leading-[1.75] text-muted-foreground m-0">
              You must be a teacher or school administrator employed by an Ontario school or school board,
              or an individual using the Service for Ontario-aligned educational purposes. You must be at
              least 18 years old and legally able to enter into contracts. By accepting these Terms you
              represent that you meet these requirements.
            </p>
          </div>

          <div>
            <h2 className="text-lg font-semibold text-foreground mb-3">3. Your Responsibilities</h2>
            <p className="text-base leading-[1.75] text-muted-foreground mb-4">
              You are solely responsible for all data you enter into the Service, including student names,
              grades, comments, and any other information. You must:
            </p>
            <ul className="list-disc list-outside pl-5 flex flex-col gap-2 text-base leading-[1.75] text-muted-foreground m-0">
              <li>Have the authority to store and process the student data you input.</li>
              <li>Comply with applicable privacy laws including <em>PIPEDA</em>, Ontario&rsquo;s <em>MFIPPA</em>, and your school board&rsquo;s data governance policies.</li>
              <li>Not enter data for any purpose other than legitimate educational reporting.</li>
              <li>Keep your account credentials confidential and notify us immediately of any unauthorized access.</li>
              <li>Not share your account with others (class-sharing features exist for that purpose).</li>
            </ul>
          </div>

          <div>
            <h2 className="text-lg font-semibold text-foreground mb-3">4. Subscriptions and Billing</h2>
            <p className="text-base leading-[1.75] text-muted-foreground mb-3">
              The Service is offered on a free plan and paid annual plans (Individual and School). Paid
              subscriptions are billed annually through Stripe. You authorize us to charge your payment
              method at the start of each subscription period.
            </p>
            <p className="text-base leading-[1.75] text-muted-foreground mb-3">
              Subscriptions renew automatically unless cancelled before the renewal date. We do not offer
              refunds for partial subscription periods. If a payment fails, access may be restricted to
              the free plan limits until payment is resolved.
            </p>
            <p className="text-base leading-[1.75] text-muted-foreground m-0">
              Prices are in Canadian dollars and do not include applicable taxes. We reserve the right to
              change pricing with 30 days&rsquo; notice to existing subscribers.
            </p>
          </div>

          <div>
            <h2 className="text-lg font-semibold text-foreground mb-3">5. Data Ownership</h2>
            <p className="text-base leading-[1.75] text-muted-foreground m-0">
              You retain full ownership of all student data and report content you create in the Service.
              We do not claim any rights to your data. We use your data only to operate, maintain, and
              improve the Service as described in our{" "}
              <a href="/privacy" className="text-primary underline underline-offset-2">Privacy Policy</a>.
              You may export or delete your data at any time through the Service.
            </p>
          </div>

          <div>
            <h2 className="text-lg font-semibold text-foreground mb-3">6. AI-Generated Content</h2>
            <p className="text-base leading-[1.75] text-muted-foreground mb-3">
              The Service includes an AI comment-generation feature powered by a third-party language
              model (Google LLC via Google Cloud Vertex AI). AI-generated comments are suggestions only. You are responsible for
              reviewing, editing, and approving all content before including it in any student record.
              We make no warranty that AI-generated content is accurate, appropriate, or free of errors.
              You must not use AI-generated comments without human review.
            </p>
            <p className="text-base leading-[1.75] text-muted-foreground m-0">
              Google Cloud Vertex AI does not use your prompts or data to train its models by default,
              and does not log prompts for model improvement. See{" "}
              <a href="https://cloud.google.com/vertex-ai/docs/generative-ai/data-governance" className="text-primary underline underline-offset-2" target="_blank" rel="noopener noreferrer">
                Google&rsquo;s data governance policy
              </a>{" "}
              for details.
            </p>
          </div>

          <div>
            <h2 className="text-lg font-semibold text-foreground mb-3">7. Intellectual Property</h2>
            <p className="text-base leading-[1.75] text-muted-foreground m-0">
              The Service&rsquo;s software, design, branding, and report card templates are the property of
              EasyReports and are protected by copyright and other intellectual property laws. You may
              not copy, modify, distribute, or reverse-engineer any part of the Service. The Ontario
              Ministry of Education report card templates are Crown copyright; their use in the Service
              is for educational assistance only.
            </p>
          </div>

          <div>
            <h2 className="text-lg font-semibold text-foreground mb-3">8. Limitation of Liability</h2>
            <p className="text-base leading-[1.75] text-muted-foreground mb-3">
              The Service is provided &ldquo;as is&rdquo; without warranty of any kind. We do not warrant that the
              Service will be uninterrupted, error-free, or free of harmful components.
            </p>
            <p className="text-base leading-[1.75] text-muted-foreground m-0">
              To the maximum extent permitted by law, EasyReports shall not be liable for any indirect,
              incidental, special, consequential, or punitive damages arising from your use of the
              Service, including loss of data. Our total liability to you for any claim shall not exceed
              the amount you paid us in the 12 months preceding the claim.
            </p>
          </div>

          <div>
            <h2 className="text-lg font-semibold text-foreground mb-3">9. Termination</h2>
            <p className="text-base leading-[1.75] text-muted-foreground m-0">
              You may close your account at any time. We may suspend or terminate your access if you
              violate these Terms, engage in fraudulent activity, or if we discontinue the Service.
              Upon termination, your data will be retained for 30 days to allow for export, after which
              it will be deleted in accordance with our Privacy Policy.
            </p>
          </div>

          <div>
            <h2 className="text-lg font-semibold text-foreground mb-3">10. Changes to These Terms</h2>
            <p className="text-base leading-[1.75] text-muted-foreground m-0">
              We may update these Terms from time to time. We will notify you by displaying a consent
              prompt on your next login when material changes occur. Continued use of the Service after
              accepting updated Terms constitutes your agreement to the new Terms.
            </p>
          </div>

          <div>
            <h2 className="text-lg font-semibold text-foreground mb-3">11. Governing Law</h2>
            <p className="text-base leading-[1.75] text-muted-foreground m-0">
              These Terms are governed by the laws of the Province of Ontario and the federal laws of
              Canada applicable therein, without regard to conflict of law principles. Any disputes
              shall be resolved in the courts of Ontario.
            </p>
          </div>

          <div>
            <h2 className="text-lg font-semibold text-foreground mb-3">12. Contact</h2>
            <p className="text-base leading-[1.75] text-muted-foreground m-0">
              Questions about these Terms?{" "}
              <a href="mailto:contact@easyreports.ca" className="text-primary underline underline-offset-2">
                contact@easyreports.ca
              </a>
            </p>
          </div>

        </div>
      </Section>
    </div>
  );
}
